'use client';

import React, { useState, useEffect } from 'react';
import { Play, Pause, Heart, MessageSquare, Share2, Zap, TrendingUp, Sparkles, Rocket, Copy, ExternalLink, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { usePlayer } from '@/contexts/PlayerContext';

export interface Track {
  id: string;
  title: string;
  artist: string;
  genre: string;
  plays: number;
  likes: number;
  comments: number;
  votes: number;
  cover: string;
  coverGradient: string;
  coverUrl?: string;
  audioUrl?: string;
  externalTrackLink?: string;
  sourcePlatform?: string;
  duration: string;
  trending: boolean;
  new: boolean;
  momentumScore?: number;
  isSnippetOnly?: boolean;
}

function formatNum(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

function ShareModal({ trackId, trackTitle, artist, onClose }: { trackId: string; trackTitle: string; artist: string; onClose: () => void }) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://echomint.io';
  // Deep-link to the specific track in the discovery feed
  const trackUrl = `${siteUrl}/music-discovery-feed?track=${trackId}`;
  const text = encodeURIComponent(`🎵 Listen to "${trackTitle}" by ${artist} on EchoMint`);
  const encodedUrl = encodeURIComponent(trackUrl);

  const shareOptions = [
    { label: 'WhatsApp', color: '#25D366', bg: 'rgba(37,211,102,0.1)', href: `https://wa.me/?text=${text}%20${encodedUrl}` },
    { label: 'X / Twitter', color: '#1DA1F2', bg: 'rgba(29,161,242,0.1)', href: `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}` },
    { label: 'Telegram', color: '#0088CC', bg: 'rgba(0,136,204,0.1)', href: `https://t.me/share/url?url=${encodedUrl}&text=${text}` },
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(trackUrl).then(() => {
      toast.success('Link copied!');
      onClose();
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-sm rounded-2xl p-5 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Share Track</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: 'var(--muted-foreground)' }}><X size={16} /></button>
        </div>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>"{trackTitle}" by {artist}</p>
        <div className="grid grid-cols-3 gap-3">
          {shareOptions.map((opt) => (
            <a key={opt.label} href={opt.href} target="_blank" rel="noopener noreferrer" onClick={onClose}
              className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all hover:opacity-80"
              style={{ background: opt.bg, border: `1px solid ${opt.color}30` }}>
              <ExternalLink size={18} style={{ color: opt.color }} />
              <span className="text-xs font-semibold" style={{ color: opt.color }}>{opt.label}</span>
            </a>
          ))}
        </div>
        <button onClick={copyLink}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
          <Copy size={15} />
          Copy Link
        </button>
      </div>
    </div>
  );
}

function CommentPanel({ trackId, onCommentAdded, onClose }: { trackId: string; onCommentAdded: () => void; onClose: () => void }) {
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const loadComments = async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from('track_comments')
        .select('*, user_profiles(full_name)')
        .eq('track_id', trackId)
        .is('parent_id', null)
        .order('created_at', { ascending: true })
        .limit(20);
      setComments(data || []);
      setLoading(false);
    };
    loadComments();
  }, [trackId]);

  const handleComment = async () => {
    if (!commentText.trim()) return;
    if (!user) { toast.error('Sign in to comment'); return; }
    setSubmitting(true);
    try {
      const supabase = createClient();
      // Insert the comment — don't rely on joined select which can fail due to RLS
      const { data: insertedRows, error } = await supabase
        .from('track_comments')
        .insert({
          track_id: trackId,
          user_id: user.id,
          content: commentText.trim(),
          parent_id: null,
        })
        .select('id, track_id, user_id, content, parent_id, created_at')
        .single();

      if (error) {
        console.error('Comment insert error:', error);
        toast.error('Failed to post comment');
        return;
      }

      // Build a local comment object with the user's display name
      const newComment = {
        ...insertedRows,
        user_profiles: { full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You' },
      };

      setComments(prev => [...prev, newComment]);
      setCommentText('');

      // Increment comments_count on tracks table
      await supabase.rpc('increment_track_comments', { track_uuid: trackId }).catch(async () => {
        // Fallback: direct increment via update
        await supabase.rpc('increment_track_comments_fallback', { track_uuid: trackId }).catch(() => {});
      });
      onCommentAdded();
      toast.success('Comment posted!');
    } catch (err: any) {
      console.error('Comment error:', err);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-xl space-y-3" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold">Comments ({comments.length})</p>
        <button onClick={onClose} style={{ color: 'var(--muted-foreground)' }}><X size={13} /></button>
      </div>
      {loading ? (
        <p className="text-xs text-center py-2" style={{ color: 'var(--muted-foreground)' }}>Loading...</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-center py-2" style={{ color: 'var(--muted-foreground)' }}>No comments yet. Be first!</p>
      ) : (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #1B2A4A, #2A9D8F)', fontSize: 9 }}>
                {(c.user_profiles?.full_name || 'U').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 rounded-lg px-2 py-1.5" style={{ background: 'var(--card)' }}>
                <p className="text-xs font-semibold" style={{ fontSize: 10 }}>{c.user_profiles?.full_name || 'User'}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)', fontSize: 11 }}>{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !submitting && handleComment()}
          placeholder="Add a comment..."
          className="flex-1 px-2.5 py-1.5 text-xs rounded-lg"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
        />
        <button
          onClick={handleComment}
          disabled={submitting || !commentText.trim()}
          className="px-2.5 py-1.5 rounded-lg disabled:opacity-50"
          style={{ background: 'var(--secondary)', color: 'white' }}
        >
          <Send size={11} />
        </button>
      </div>
    </div>
  );
}

export default function TrackCard({ track }: { track: Track }) {
  const [liked, setLiked] = useState(false);
  const [localLikes, setLocalLikes] = useState(track.likes);
  const [localComments, setLocalComments] = useState(track.comments);
  const [showComments, setShowComments] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [likeChecked, setLikeChecked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const { user } = useAuth();
  const router = useRouter();
  const { playTrack, currentTrack, isPlaying, togglePlay } = usePlayer();

  const audioUrl = track.audioUrl;
  const hasAudio = !!(audioUrl && audioUrl.startsWith('http'));
  const isCurrentTrack = currentTrack?.id === track.id;

  // Check if user already liked this track
  useEffect(() => {
    if (!user || likeChecked) return;
    const checkLike = async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from('track_likes')
        .select('id')
        .eq('track_id', track.id)
        .eq('user_id', user.id)
        .maybeSingle();
      setLiked(!!data);
      setLikeChecked(true);
    };
    checkLike();
  }, [user, track.id, likeChecked]);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Sign in to like tracks');
      return;
    }
    if (likeLoading) return;
    setLikeLoading(true);

    const newLiked = !liked;
    // Optimistic update
    setLiked(newLiked);
    setLocalLikes(prev => newLiked ? prev + 1 : Math.max(0, prev - 1));
    if (newLiked) toast.success(`Liked "${track.title}"`);

    try {
      const supabase = createClient();
      if (newLiked) {
        const { error } = await supabase.from('track_likes').insert({ track_id: track.id, user_id: user.id });
        if (error && error.code === '23505') {
          // Already liked — revert optimistic update
          setLiked(true);
          setLocalLikes(prev => prev - 1);
          setLikeLoading(false);
          return;
        }
        if (error) {
          // Revert on other errors
          setLiked(false);
          setLocalLikes(prev => prev - 1);
          setLikeLoading(false);
          return;
        }
        // Try RPC first, then direct count update as fallback
        const { error: rpcError } = await supabase.rpc('increment_track_likes', { track_uuid: track.id });
        if (rpcError) {
          // Fallback: count actual likes and update
          const { count } = await supabase
            .from('track_likes')
            .select('*', { count: 'exact', head: true })
            .eq('track_id', track.id);
          if (count !== null) {
            await supabase.from('tracks').update({ likes_count: count }).eq('id', track.id);
          }
        }
      } else {
        await supabase.from('track_likes').delete().eq('track_id', track.id).eq('user_id', user.id);
        const { error: rpcError } = await supabase.rpc('decrement_track_likes', { track_uuid: track.id });
        if (rpcError) {
          // Fallback: count actual likes and update
          const { count } = await supabase
            .from('track_likes')
            .select('*', { count: 'exact', head: true })
            .eq('track_id', track.id);
          if (count !== null) {
            await supabase.from('tracks').update({ likes_count: count }).eq('id', track.id);
          }
        }
      }
    } catch {
      // Revert on error
      setLiked(!newLiked);
      setLocalLikes(prev => newLiked ? prev - 1 : prev + 1);
    } finally {
      setLikeLoading(false);
    }
  };

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasAudio) {
      toast.info('No preview available for this track');
      return;
    }
    if (isCurrentTrack) {
      togglePlay();
    } else {
      playTrack({
        id: track.id,
        title: track.title,
        artist: track.artist,
        coverUrl: track.coverUrl,
        audioUrl: audioUrl!,
      }, false);
    }
  };

  const handleSupport = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Sign in to support campaigns');
      router.push('/sign-up-login-screen');
      return;
    }
    router.push('/user-dashboard?section=wallet');
    toast.success(`Find "${track.title}" in Discover Campaigns`);
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { toast.error('Sign in to comment'); return; }
    setShowComments(!showComments);
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden card-hover group">
      {/* Cover */}
      <div className="relative h-40 cursor-pointer" onClick={handlePlay}
        style={{ background: track.coverGradient }}>
        {track.coverUrl ? (
          <img src={track.coverUrl} alt={`${track.title} cover art`} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl font-bold text-white/20">{track.cover}</span>
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100"
          style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(42,157,143,0.35)', backdropFilter: 'blur(8px)', border: '2px solid rgba(42,157,143,0.6)' }}>
            {isCurrentTrack && isPlaying
              ? <Pause size={22} className="text-white" />
              : <Play size={24} className="text-white ml-1" />}
          </div>
        </div>

        {/* Snippet label */}
        <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: 'rgba(42,157,143,0.8)', color: 'white' }}>
            🎵 30s Preview
          </span>
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          {track.trending && (
            <span className="flex items-center gap-1 badge-purple text-xs px-2 py-0.5 rounded-full font-semibold">
              <TrendingUp size={10} /> Trending
            </span>
          )}
          {track.new && (
            <span className="flex items-center gap-1 badge-cyan text-xs px-2 py-0.5 rounded-full font-semibold">
              <Sparkles size={10} /> New
            </span>
          )}
        </div>

        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded"
            style={{ background: 'rgba(0,0,0,0.6)', color: 'white' }}>
            {track.duration}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <h3 className="font-semibold text-sm truncate">{track.title}</h3>
            <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--muted-foreground)' }}>
              {track.artist}
            </p>
          </div>
          <span className="badge-purple text-xs px-2 py-0.5 rounded-full flex-shrink-0">{track.genre}</span>
        </div>

        {/* Momentum score */}
        {track.momentumScore !== undefined && (
          <div className="flex items-center gap-1.5 mt-2 mb-1">
            <Zap size={11} style={{ color: 'var(--secondary)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--secondary)' }}>
              Momentum: {track.momentumScore}
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 mt-3">
          <button onClick={handleLike}
            disabled={likeLoading}
            className="flex items-center gap-1.5 text-xs transition-colors disabled:opacity-70"
            style={{ color: liked ? '#EC4899' : 'var(--muted-foreground)' }}>
            <Heart size={14} fill={liked ? 'currentColor' : 'none'} />
            <span className="tabular-nums">{formatNum(localLikes)}</span>
          </button>
          <button onClick={handleCommentClick}
            className="flex items-center gap-1.5 text-xs transition-colors"
            style={{ color: showComments ? 'var(--secondary)' : 'var(--muted-foreground)' }}>
            <MessageSquare size={14} />
            <span className="tabular-nums">{formatNum(localComments)}</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowShareModal(true); }}
            className="flex items-center gap-1.5 text-xs transition-colors"
            style={{ color: 'var(--muted-foreground)' }}>
            <Share2 size={14} />
          </button>
          <button
            onClick={handleSupport}
            className="ml-auto flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all"
            style={{ background: 'rgba(42,157,143,0.1)', color: 'var(--secondary)', border: '1px solid rgba(42,157,143,0.2)' }}
          >
            <Rocket size={12} />
            Support
          </button>
        </div>

        {/* Comment panel */}
        {showComments && (
          <CommentPanel
            trackId={track.id}
            onCommentAdded={() => setLocalComments(prev => prev + 1)}
            onClose={() => setShowComments(false)}
          />
        )}
      </div>

      {/* Share modal */}
      {showShareModal && (
        <ShareModal
          trackId={track.id}
          trackTitle={track.title}
          artist={track.artist}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}